import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HandleChange from "../../../../../Helpers/HandleChange";
import { GetAllMealForecast } from "../../../../../reduxToolkit/FoodieMenu/MenuApi";
import {MEAL_FORECAST_MODEL} from "../Model/Model";

/**
 * Hook-like component (custom logic provider) that manages the state, filters,
 * and data fetching for the meal forecast feature.
 *
 * @Email   : kpozos@ibl.mx
 * @Author  : Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer
 *
 * @param {Function} setResults - Callback to update the results list when data is fetched (required).
 * @param {Function} setBodyForExport - Callback to set the export payload for the report (required).
 * @param setSearch - Optional callback to update the search state in the parent
 * component. Can be used to synchronize search/filter values outside of this hook-like component.
 */
export const MealForecastApp = ({ setResults, setBodyForExport }) => {
    const dispatch = useDispatch();
    // Ref to measure position of an element in layout (used for custom CSS positioning)
    const elementRefTab1 = useRef(null);
    // Redux: get cafeterias from global state
    const { allCafeterias } = useSelector(state => state.FoodieShiftSlice);
    // Cafeteria options mapped into {id, name} for dropdowns or selectors
    const cafeteriasOptions = allCafeterias?.map((item) => ({
        id: item.id,
        name: item.name
    })) || [];

    // Local state for filters: cafeteria selection + date range (default today)
    const [data, setData] = useState({...MEAL_FORECAST_MODEL});

    // Utility handler for controlled inputs
    const { handleChange } = HandleChange({ model: data, setModel: setData });

    /**
     * Layout effect for adjusting custom CSS variables dynamically.
     * This is mainly for aligning UI elements based on their position on screen.
     */
    useLayoutEffect(() => {
        if (elementRefTab1?.current) {
            const rect = elementRefTab1.current.getBoundingClientRect();
            const distanceTop = rect.top;
            // Inject value into a CSS custom property
            elementRefTab1.current.style.setProperty('--top-value', `${distanceTop}px`);
        }
    }, [elementRefTab1?.current]);

    /**
     * Effect: triggers data fetch whenever the filter state changes.
     */
    useEffect(() => {
        getData();
    }, [data]);

    /**
     * Fetch meal forecast data based on current filter criteria.
     * - Builds request body from cafeteria + date range.
     * - Dispatches async redux action to retrieve data.
     * - Updates export payload and results list.
     */
    const getData = () => {
        if (!data.cafeteriaId) return;
        const body = data.toRegister();

        // Save body for future export (e.g., CSV, Excel, PDF)
        setBodyForExport( prev => ({
            ...prev,
            cafeteriaId: body.cafeteriaId,
            attendanceStartDate: body.since,
            attendanceEndDate: body.to,
        }));

        // Fetch forecast data from API
        dispatch(GetAllMealForecast(body)).then(
            ({ payload: { data: { data, success } } }) => {
                if (success === true) {
                    setResults(data);
                }
            }
        );
    };

    // Expose values & handlers so parent component can use them
    return {
        cafeteriasOptions,
        data,
        setData,
        handleChange
    };
};
